import { Fragment, useId, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { centsFromDecimal, formatCurrency, isCurrencyCode } from '@eis/core';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { breakdownGroups, lineNameOf, type BreakdownRow } from '../model/breakdown';
import {
  currencyOfPremium,
  formatAmount,
  formatRate,
  taxModeOfPremium,
  totalsOfPremium,
} from '../model/format';

/**
 * What the premium is made of (SDD-021 §9) — the operations persona's answer to "why this
 * number". It replaces prompt 15.4's `PremiumBreakdown` and keeps its two rules: nothing is
 * recomputed (the lines are frozen onto the snapshot), and the lines are named by the words a
 * product owner chose, never by an expression or the asset they came from (SSOT I-6).
 *
 * What SDD-021 adds:
 *  - the lines are GROUPED by their `group` label and say what they are — a product line, a group
 *    factor line, a tax line;
 *  - an INCLUDED premium's tax line is *darin enthalten*: contained in the gross, not added below;
 *  - a switch *Hilfswerte anzeigen* shows the HIDDEN helpers in a muted style with a *Hilfswert*
 *    label. They are never part of a sum: the sum rows are the premium's own net, tax and gross,
 *    whatever the switch says. A snapshot back-filled from before helpers were kept says so.
 *
 * Renders NOTHING for a premium without lines (a policy that predates the breakdown): an empty
 * section with a heading would suggest something is missing rather than that the policy is older.
 */
export function PremiumBreakdownTable({
  premium,
  locale,
}: {
  readonly premium: InstanceApi.Premium | undefined;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const id = useId();
  const [showHelpers, setShowHelpers] = useState(false);
  const items = premium?.items ?? [];
  if (items.length === 0) return null;

  const helpersStored = premium?.helpersStored !== false;
  const groups = breakdownGroups(items, showHelpers && helpersStored);
  const currency = currencyOfPremium(premium);
  const totals = totalsOfPremium(premium);
  const included = taxModeOfPremium(premium) === 'INCLUDED';
  const titleId = `${id}-title`;

  return (
    <Box component="section" aria-labelledby={titleId}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h3" id={titleId}>
            {t('premium.breakdown.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('premium.breakdown.hint')}
          </Typography>
        </Box>
        <Stack sx={{ alignItems: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showHelpers && helpersStored}
                disabled={!helpersStored}
                onChange={(event) => {
                  setShowHelpers(event.target.checked);
                }}
              />
            }
            label={t('premium.breakdown.helpers')}
          />
          {!helpersStored && (
            <Typography variant="caption" color="text.secondary" data-testid="helpers-not-stored">
              {t('premium.breakdown.helpersNotStored')}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Table size="small" aria-labelledby={titleId} sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('premium.breakdown.item')}</TableCell>
            <TableCell>{t('premium.breakdown.kind')}</TableCell>
            <TableCell align="right">{t('premium.breakdown.amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <Fragment key={group.group === '' ? '·' : group.group}>
              {/* A group heading only when there is more than one group to tell apart. */}
              {groups.length > 1 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {group.group === '' ? t('premium.breakdown.ungrouped') : group.group}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {group.rows.map((row, index) => (
                <LineRow
                  key={row.item.key ?? `${group.group}-${String(index)}`}
                  row={row}
                  locale={locale}
                />
              ))}
            </Fragment>
          ))}

          {/* The sums: the premium's own figures — never a total over the rows above. */}
          <TableRow data-testid="breakdown-sum-net">
            <TableCell
              colSpan={2}
              sx={{ fontWeight: 600, borderTop: 2, borderTopColor: 'divider' }}
            >
              {t('premium.net')}
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, borderTop: 2, borderTopColor: 'divider' }}
            >
              {formatAmount(totals.net, currency, locale)}
            </TableCell>
          </TableRow>
          {(totals.tax ?? []).map((tax) => (
            <TableRow key={tax.code ?? ''} data-testid="breakdown-sum-tax">
              <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                {t('premium.taxLine', { code: tax.code ?? '', rate: formatRate(tax.rate, locale) })}
                {included && <IncludedMark />}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatAmount(tax.amount, currency, locale)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow data-testid="breakdown-sum-gross">
            <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
              {t('premium.gross')}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatAmount(totals.gross, currency, locale)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

function IncludedMark(): ReactNode {
  const { t } = useTranslation('insurance');
  return (
    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
      {t('premium.included')}
    </Typography>
  );
}

function LineRow({
  row,
  locale,
}: {
  readonly row: BreakdownRow;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const total = row.item.itemType === 'TOTAL';
  const muted = row.helper ? { color: 'text.secondary', fontStyle: 'italic' } : {};
  return (
    <TableRow data-testid={row.helper ? 'breakdown-helper' : 'breakdown-line'}>
      <TableCell sx={muted}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography
            variant="body2"
            component="span"
            sx={{ fontWeight: total ? 600 : 400, ...muted }}
          >
            {lineNameOf(row.item)}
          </Typography>
          {total && <Chip size="small" color="primary" label={t('premium.breakdown.total')} />}
          {row.helper && (
            <Tooltip title={t('premium.breakdown.helperHint')}>
              <Chip size="small" variant="outlined" label={t('premium.breakdown.helper')} />
            </Tooltip>
          )}
          {row.included && <IncludedMark />}
        </Stack>
      </TableCell>
      <TableCell sx={muted}>{t(`premium.breakdown.role.${row.role}`)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: total ? 600 : 400, ...muted }}>
        {lineAmount(row.item, locale)}
      </TableCell>
    </TableRow>
  );
}

/**
 * One line's amount, formatted for what it IS: an ISO-4217 unit is money (`formatCurrency`),
 * anything else — a factor, a percentage — is the number with its unit beside it. A value that
 * does not parse is shown as it came.
 */
function lineAmount(item: InstanceApi.PremiumItem, locale: string): string {
  const amount = item.amount ?? '';
  const unit = item.unit ?? '';
  const cents = centsFromDecimal(amount);
  if (cents !== null && isCurrencyCode(unit)) return formatCurrency(cents, unit, locale);
  return unit === '' ? amount : `${amount} ${unit}`;
}
